import React, { useState, useEffect, useCallback } from "react";
import Layout from "../Components/Layout";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  useMediaQuery,
  useTheme,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  AppBar,
  Toolbar,
  Slide,
  Collapse,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import ForumIcon from "@mui/icons-material/Forum";
import ScienceIcon from "@mui/icons-material/Science";
import VoiceChatIcon from "@mui/icons-material/VoiceChat";
import WidgetsIcon from "@mui/icons-material/Widgets";
import Homeicon from "@mui/icons-material/Home";
import IntegrationIcon from "@mui/icons-material/Extension";
// import PackagesIcon from "@mui/icons-material/Category";
import { BsBoxFill } from "react-icons/bs";

import Widget from "./Widget";
import Voices from "./Voices";
import Packages from "./Packages";
import PromptSettings from "./PromptSettings";
import ChangePassword from "./ChangePassword";
import ToolsSettings from "./ToolsSettings";
// import TestZone from "./TestZone";
import Integration from "../pages/Integration/Integration";
import UserDashboard from "./UserDashboard";
import Conversations from "./Conversations";
import { forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import useCallOnce from "../hooks/useCallOnce";
import { getUserListData } from "../redux/actions/userActions";
import { getUsersListForDropdown } from "../redux/actions/userActions";

// Full-screen dialog slide transition (Material UI pattern)
const FullScreenTransition = forwardRef(function FullScreenTransition(
  props,
  ref
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function Users() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { list, loading, pagination, dropdown } = useSelector(
    (state) => state.users
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    company: "",
    email: "",
    storeUrl: "",
    userId: "",
    is_connected_to_brand: "",
    shopify_connection: "",
    plan: "",
  });
  const [userInput, setUserInput] = useState("");
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailModalOpen, setUserDetailModalOpen] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  useCallOnce(
    "users/initial-load",
    () => {
      const apiPage = 1;
      dispatch(getUserListData({ page: apiPage, limit: rowsPerPage }));
    },
    [dispatch]
  );

  useCallOnce(
    "users/dropdown",
    () => {
      const apiPage = 1;
      dispatch(getUsersListForDropdown("", apiPage, 20));
    },
    [dispatch]
  );

  // Debounced search for user dropdown
  const debouncedSearch = useCallback(
    (query) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const timer = setTimeout(() => {
        if (query.length >= 2) {
          dispatch(getUsersListForDropdown(query, 1, 20));
        }
      }, 300);

      setDebounceTimer(timer);
    },
    [debounceTimer, dispatch]
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const apiPage = newPage + 1;
    dispatch(
      getUserListData({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        company: filters.company || undefined,
        email: filters.email || undefined,
        storeUrl: filters.storeUrl || undefined,
        userId: filters.userId || undefined,
        is_connected_to_brand:
          filters.is_connected_to_brand === "true"
            ? true
            : filters.is_connected_to_brand === "false"
            ? false
            : undefined,
        shopify_connection: filters.shopify_connection || undefined,
        plan: filters.plan || undefined,
        page: apiPage,
        limit: rowsPerPage,
      })
    );
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    dispatch(
      getUserListData({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        company: filters.company || undefined,
        email: filters.email || undefined,
        storeUrl: filters.storeUrl || undefined,
        userId: filters.userId || undefined,
        is_connected_to_brand:
          filters.is_connected_to_brand === "true"
            ? true
            : filters.is_connected_to_brand === "false"
            ? false
            : undefined,
        shopify_connection: filters.shopify_connection || undefined,
        plan: filters.plan || undefined,
        page: 1,
        limit: newRowsPerPage,
      })
    );
  };

  // Basic column extractor with safe access
  const extractRow = (item) => {
    return {
      company: item.company || item.org || item.account || "-",
      email: item.email || item.userEmail || "-",
      storeUrl: item.storeUrl || item.store_url || item.shop_url || "-",
      userId: item.userId || item._id || item.id || "-",
      createdAt: item.createdAt || item.created_at || item.joinedAt || "-",
      has_plan_selected: item.has_plan_selected,
    };
  };

  // Server-side pagination: use list directly as it contains the current page data
  const pagedRows = list;

  // Helpers to render date as MM/DD/YYYY in TextField while storing ISO (YYYY-MM-DD)
  const formatDateForUi = (isoDateString) => {
    if (!isoDateString) return "";
    const [year, month, day] = isoDateString.split("-");
    if (!year || !month || !day) return "";
    return `${month}/${day}/${year}`;
  };

  // UI Date Field: same UX as Conversations
  const UiDateField = ({ label, value, onChange, min, max }) => {
    const hiddenDateRef = React.useRef(null);

    const openPicker = () => {
      const el = hiddenDateRef.current;
      if (!el) return;
      try {
        el.focus();
      } catch (_) {}
      if (typeof el.showPicker === "function") {
        try {
          el.showPicker();
        } catch (_) {}
      } else {
        try {
          el.click();
        } catch (_) {}
      }
    };

    return (
      <Box
        sx={{ position: "relative" }}
        onMouseDown={(e) => {
          e.preventDefault();
          openPicker();
        }}
      >
        <TextField
          fullWidth
          label={label}
          type="text"
          placeholder={"MM/DD/YYYY"}
          value={formatDateForUi(value || "")}
          onFocus={openPicker}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
          inputProps={{ readOnly: true }}
        />

        <input
          ref={hiddenDateRef}
          type="date"
          value={value || ""}
          min={min}
          max={max}
          onChange={(e) => onChange(e.target.value || "")}
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0,
            width: "100%",
            height: "100%",
            cursor: "pointer",
            zIndex: 10,
            pointerEvents: "auto",
          }}
        />
      </Box>
    );
  };

  const renderDate = (value) => {
    if (!value) return "-";
    const date =
      typeof value === "number" ? new Date(value * 1000) : new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/New_York",
    }).format(date);
  };

  function createData(name, calories, fat, carbs, protein) {
    return { name, calories, fat, carbs, protein };
  }
  const rows = [
    createData("Frozen yoghurt", 159, 6.0, 24, 4.0),
    createData("Ice cream sandwich", 237, 9.0, 37, 4.3),
    createData("Eclair", 262, 16.0, 24, 6.0),
    createData("Cupcake", 305, 3.7, 67, 4.3),
    createData("Gingerbread", 356, 16.0, 49, 3.9),
  ];

  const handleOpenUserDetail = (item) => {
    setSelectedUser(item);
    setActiveTab(0);
    setUserDetailModalOpen(true);
  };

  const handleCloseUserDetail = () => {
    setUserDetailModalOpen(false);
  };

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  return (
    <Layout>
      <Box sx={{ mt: 0, px: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "start",
            flexDirection: "column",
            alignItems: "start",
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Brands
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Manage and view brands
            {/* Create and manage your AI agents */}
          </Typography>
        </Box>
        {/* Filters Row (MUI) */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UiDateField
                label="Start Date"
                value={filters.startDate}
                onChange={(val) =>
                  setFilters((f) => ({ ...f, startDate: val }))
                }
                max={filters.endDate || undefined}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UiDateField
                label="End Date"
                value={filters.endDate}
                onChange={(val) => setFilters((f) => ({ ...f, endDate: val }))}
                min={filters.startDate || undefined}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                size="small"
                fullWidth
                options={dropdown.users || []}
                loading={Boolean(dropdown?.loading)}
                loadingText="Loading..."
                value={selectedUserOption}
                getOptionLabel={(u) =>
                  u?.email ||
                  u?.name ||
                  u?.company ||
                  u?._id ||
                  u?.userId ||
                  u?.id ||
                  ""
                }
                isOptionEqualToValue={(a, b) =>
                  (a?._id || a?.userId || a?.id) ===
                  (b?._id || b?.userId || b?.id)
                }
                onChange={(e, value) => {
                  const id = value ? value._id || value.userId || value.id : "";
                  setFilters((f) => ({ ...f, userId: id }));
                  setSelectedUserOption(value || null);
                }}
                inputValue={userInput}
                onInputChange={(e, value, reason) => {
                  setUserInput(value);
                  // Only trigger API on user typing, not on option select/clear
                  if (reason === "input") {
                    debouncedSearch(value || "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Brand"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dropdown?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={16}
                              sx={{ mr: 1 }}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
              <TextField
                fullWidth
                label="Email"
                placeholder="Email"
                value={filters.email}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, email: e.target.value }))
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
              <TextField
                fullWidth
                label="Company"
                placeholder="Company"
                value={filters.company}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, company: e.target.value }))
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid
              size={{ xs: 12, sm: 6, md: 3, lg: 3 }}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              <TextField
                fullWidth
                label="Store URL"
                placeholder="Store URL"
                value={filters.storeUrl}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, storeUrl: e.target.value }))
                }
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.875rem" } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: "0.875rem" }}>Plans</InputLabel>
                <Select
                  value={filters.plan}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, plan: e.target.value }))
                  }
                  label="Plans"
                  sx={{
                    fontSize: "0.875rem",
                  }}
                >
                  {/* <MenuItem value="">All</MenuItem> */}
                  <MenuItem value="selected">Selected</MenuItem>
                  <MenuItem value="notselected">Not Selected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: "0.875rem" }}>
                  App Status
                </InputLabel>
                <Select
                  value={filters.is_connected_to_brand}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      is_connected_to_brand: e.target.value,
                    }))
                  }
                  label="App Status"
                  sx={{
                    fontSize: "0.875rem",
                  }}
                >
                  {/* <MenuItem value="">All</MenuItem> */}
                  <MenuItem value="true">Connected</MenuItem>
                  <MenuItem value="false">Disconnected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: "0.875rem" }}>
                  Shopify Status
                </InputLabel>
                <Select
                  value={filters.shopify_connection}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      shopify_connection: e.target.value,
                    }))
                  }
                  label="Shopify Status"
                  sx={{
                    fontSize: "0.875rem",
                  }}
                >
                  {/* <MenuItem value="">All</MenuItem> */}
                  <MenuItem value="connected">Connected</MenuItem>
                  <MenuItem value="notconnected">Disconnected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexDirection: { xs: "column", md: "row" },
                  "& > *": {
                    flex: { xs: 1, md: "none" },
                  },
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    setPage(0);
                    dispatch(
                      getUserListData({
                        startDate: filters.startDate || undefined,
                        endDate: filters.endDate || undefined,
                        company: filters.company || undefined,
                        email: filters.email || undefined,
                        storeUrl: filters.storeUrl || undefined,
                        userId: filters.userId || undefined,
                        is_connected_to_brand:
                          filters.is_connected_to_brand === "true"
                            ? true
                            : filters.is_connected_to_brand === "false"
                            ? false
                            : undefined,
                        shopify_connection:
                          filters.shopify_connection || undefined,
                        plan: filters.plan || undefined,
                        page: 1,
                        limit: rowsPerPage,
                      })
                    );
                  }}
                  size="medium"
                  sx={{
                    bgcolor: "#111827",
                    "&:hover": { bgcolor: "#0b1220" },
                    textTransform: "none",
                  }}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const reset = {
                      startDate: "",
                      endDate: "",
                      company: "",
                      email: "",
                      storeUrl: "",
                      userId: "",
                      is_connected_to_brand: "",
                      shopify_connection: "",
                      plan: "",
                    };
                    setFilters(reset);
                    setUserInput("");
                    setSelectedUserOption(null);
                    setPage(0);
                    dispatch(getUsersListForDropdown("", 1, 20));
                    dispatch(getUserListData({ page: 1, limit: rowsPerPage }));
                  }}
                  size="medium"
                  sx={{
                    borderColor: "#111827",
                    color: "#111827",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#0b1220",
                      backgroundColor: "#f9fafb",
                    },
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table
            stickyHeader
            sx={{
              tableLayout: "fixed",
              minWidth: 720,
              "& td, & th": {
                px: { xs: 1, sm: 2 },
                py: { xs: 0.75, sm: 1 },
              },
              "& td .MuiTypography-root": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 70,
                    maxWidth: 70,
                    width: 70,
                  }}
                >
                  S. No
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 160,
                    maxWidth: 160,
                    width: 160,
                  }}
                >
                  Company
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 200,
                    maxWidth: 200,
                    width: 200,
                  }}
                >
                  Email
                </TableCell>

                <TableCell
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 180,
                    maxWidth: 180,
                    width: 180,
                  }}
                >
                  Store URL
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 160,
                    maxWidth: 160,
                    width: 160,
                  }}
                >
                  Created At
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 120,
                    maxWidth: 120,
                    width: 120,
                  }}
                >
                  Plans
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 140,
                    maxWidth: 140,
                    width: 140,
                  }}
                >
                  App Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f8fafc",
                    py: 1,
                    minWidth: 140,
                    maxWidth: 140,
                    width: 140,
                  }}
                >
                  Shopify Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ textAlign: "center", py: 8, height: 300 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body1" sx={{ color: "#6b7280" }}>
                        Loading users...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : list.length > 0 ? (
                pagedRows.map((item, index) => {
                  const row = extractRow(item);
                  return (
                    <TableRow
                      key={row.userId || index}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#f8fafc" },
                      }}
                      onClick={() => handleOpenUserDetail(item)}
                    >
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "#374151", fontWeight: 500 }}
                        >
                          {row.company}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {row.email}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1,
                          display: { xs: "none", md: "table-cell" },
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: "#6b7280",
                            cursor: row.storeUrl && row.storeUrl !== "-" ? "pointer" : "default",
                            textDecoration: row.storeUrl && row.storeUrl !== "-" ? "underline" : "none",
                            "&:hover": {
                              color: row.storeUrl && row.storeUrl !== "-" ? "#111827" : "#6b7280"
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (row.storeUrl && row.storeUrl !== "-") {
                              const url = row.storeUrl.startsWith('http') ? row.storeUrl : `https://${row.storeUrl}`;
                              window.open(url, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        >
                          {row.storeUrl}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {renderDate(row.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Chip
                          label={
                            item.has_plan_selected === true
                              ? "Selected"
                              : "Not Selected"
                          }
                          color={
                            item.has_plan_selected === true
                              ? "success"
                              : "default"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Chip
                          label={
                            item.is_connected_to_brand === true
                              ? "Connected"
                              : "Disconnected"
                          }
                          color={
                            item.is_connected_to_brand === true
                              ? "success"
                              : "error"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Chip
                          label={
                            item.shopify && item.shopify.storeUrl
                              ? "Connected"
                              : "Disconnected"
                          }
                          color={
                            item.shopify && item.shopify.storeUrl
                              ? "success"
                              : "error"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination.totalCount || list.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid #e5e7eb",
            "& .MuiTablePagination-toolbar": {
              paddingLeft: 2,
              paddingRight: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.875rem",
                color: "#6b7280",
              },
            "& .MuiTablePagination-select": {
              fontSize: "0.875rem",
            },
          }}
        />
      </Box>

      {/* User Detail Modal */}
      <Dialog
        open={userDetailModalOpen}
        onClose={handleCloseUserDetail}
        fullScreen
        TransitionComponent={FullScreenTransition}
        TransitionProps={{ onExited: () => setSelectedUser(null) }}
      >
        <AppBar
          position="sticky"
          color="default"
          elevation={0}
          sx={{ borderBottom: "1px solid #e5e7eb" }}
        >
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={handleCloseUserDetail}
                edge="start"
                size="small"
                sx={{ mr: 1 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedUser
                  ? extractRow(selectedUser).company
                  : "Brand Details"}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseUserDetail} edge="end" size="small">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            height: "100%",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Side Menu */}
          <Box
            sx={{
              width: { xs: "100%", md: 250 },
              borderRight: { xs: "none", md: "1px solid #e5e7eb" },
              borderBottom: { xs: "1px solid #e5e7eb", md: "none" },
              backgroundColor: "#f9fafb",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
              <Typography
                variant="subtitle2"
                sx={{ color: "#6b7280", fontWeight: 600 }}
              >
                Brand Details
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                py: 1,
                display: { xs: "grid", md: "block" },
                gridTemplateColumns: { xs: "1fr 1fr" },
                gap: { xs: 1, md: 0 },
              }}
            >
              {[
                { label: "Dashboard", icon: <Homeicon fontSize="small" /> },
                { label: "Widget", icon: <WidgetsIcon fontSize="small" /> },
                {
                  label: "Conversations",
                  icon: <ForumIcon fontSize="small" />,
                },
                { label: "Voices", icon: <VoiceChatIcon fontSize="small" /> },
                // { label: "Test Zone", icon: <ScienceIcon fontSize="small" /> },
                {
                  label: "Integration",
                  icon: <IntegrationIcon fontSize="small" />,
                },
                {
                  label: "Packages",
                  icon: <BsBoxFill fontSize="medium" />,
                },
              ].map((item, index) => (
                <Box
                  key={index}
                  onClick={() => handleTabClick(index)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    width: "100%",
                    backgroundColor:
                      activeTab === index ? "#111827" : "transparent",
                    color: activeTab === index ? "white" : "#374151",
                    "&:hover": {
                      backgroundColor:
                        activeTab === index ? "#111827" : "#f3f4f6",
                    },
                    transition: "all 0.2s ease",
                    borderRadius: { xs: 1, md: 0 },
                    borderLeft: {
                      md:
                        activeTab === index
                          ? "3px solid #111827"
                          : "3px solid transparent",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      mr: 1.5,
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {item.icon}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: activeTab === index ? 600 : 400 }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}

              {/* Settings Dropdown */}
              <Box>
                <Box
                  onClick={() => setSettingsExpanded(!settingsExpanded)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    width: "100%",
                    backgroundColor:
                      activeTab === 6 || activeTab === 7 || activeTab === 8
                        ? "#111827"
                        : "transparent",
                    color:
                      activeTab === 6 || activeTab === 7 || activeTab === 8
                        ? "white"
                        : "#374151",
                    "&:hover": {
                      backgroundColor:
                        activeTab === 6 || activeTab === 7 || activeTab === 8
                          ? "#111827"
                          : "#f3f4f6",
                    },
                    transition: "all 0.2s ease",
                    borderRadius: { xs: 1, md: 0 },
                    borderLeft: {
                      md:
                        activeTab === 6 || activeTab === 7 || activeTab === 8
                          ? "3px solid #111827"
                          : "3px solid transparent",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      sx={{
                        mr: 1.5,
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight:
                          activeTab === 6 || activeTab === 7 || activeTab === 8
                            ? 600
                            : 400,
                      }}
                    >
                      Settings
                    </Typography>
                  </Box>
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: settingsExpanded
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </Box>

                <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
                  <Box>
                    <Box
                      onClick={() => handleTabClick(6)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        pl: 5,
                        pr: 2,
                        py: 1.5,
                        cursor: "pointer",
                        width: "100%",
                        backgroundColor:
                          activeTab === 6 ? "#111827" : "transparent",
                        color: activeTab === 6 ? "white" : "#374151",
                        "&:hover": {
                          backgroundColor:
                            activeTab === 6 ? "#111827" : "#f3f4f6",
                        },
                        transition: "all 0.2s ease",
                        borderRadius: { xs: 1, md: 0 },
                      }}
                    >
                      <Typography
                        sx={{
                          mr: 1.5,
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <ScienceIcon fontSize="small" />
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: activeTab === 6 ? 600 : 400 }}
                      >
                        Prompt Settings
                      </Typography>
                    </Box>

                    <Box
                      onClick={() => handleTabClick(7)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        pl: 5,
                        pr: 2,
                        py: 1.5,
                        cursor: "pointer",
                        width: "100%",
                        backgroundColor:
                          activeTab === 7 ? "#111827" : "transparent",
                        color: activeTab === 7 ? "white" : "#374151",
                        "&:hover": {
                          backgroundColor:
                            activeTab === 7 ? "#111827" : "#f3f4f6",
                        },
                        transition: "all 0.2s ease",
                        borderRadius: { xs: 1, md: 0 },
                      }}
                    >
                      <Typography
                        sx={{
                          mr: 1.5,
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <LockIcon fontSize="small" />
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: activeTab === 7 ? 600 : 400 }}
                      >
                        Change Password
                      </Typography>
                    </Box>

                    <Box
                      onClick={() => handleTabClick(8)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        pl: 5,
                        pr: 2,
                        py: 1.5,
                        cursor: "pointer",
                        width: "100%",
                        backgroundColor:
                          activeTab === 8 ? "#111827" : "transparent",
                        color: activeTab === 8 ? "white" : "#374151",
                        "&:hover": {
                          backgroundColor:
                            activeTab === 8 ? "#111827" : "#f3f4f6",
                        },
                        transition: "all 0.2s ease",
                        borderRadius: { xs: 1, md: 0 },
                      }}
                    >
                      <Typography
                        sx={{
                          mr: 1.5,
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <SettingsIcon fontSize="small" />
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: activeTab === 8 ? 600 : 400 }}
                      >
                        Tools Settings
                      </Typography>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Box>

          {/* Content Area */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {/* Scoped style to hide global app chrome when embedding full pages */}
            <style>{`
              .embedded-page header,
              .embedded-page nav[aria-label="sidebar"],
              .embedded-page .MuiDrawer-root,
              .embedded-page .MuiDrawer-paper {
                display: none !important;
              }
            `}</style>
            <Box className="embedded-page" sx={{ p: 0, height: "100%" }}>
              {activeTab === 0 && (
                <UserDashboard
                  selectedUserId={
                    selectedUser?._id ||
                    selectedUser?.userId ||
                    selectedUser?.id ||
                    undefined
                  }
                />
              )}
              {activeTab === 1 && (
                <Widget
                  selectedUserId={
                    selectedUser?._id ||
                    selectedUser?.userId ||
                    selectedUser?.id ||
                    undefined
                  }
                />
              )}
              {activeTab === 2 && (
                <Conversations
                  selectedUserId={
                    selectedUser?._id ||
                    selectedUser?.userId ||
                    selectedUser?.id ||
                    undefined
                  }
                />
              )}
              {activeTab === 3 && (
                <Voices
                  embedded={true}
                  userId={
                    selectedUser?._id ||
                    selectedUser?.userId ||
                    selectedUser?.id ||
                    undefined
                  }
                />
              )}
              {/* {activeTab === 4 && <TestZone />} */}
              {activeTab === 4 && (
                <Integration
                  selectedUserId={
                    selectedUser?._id ||
                    selectedUser?.userId ||
                    selectedUser?.id ||
                    undefined
                  }
                />
              )}
              {activeTab === 5 && (
                <Packages
                  selectedUserId={
                    selectedUser?._id ||
                    selectedUser?.userId ||
                    selectedUser?.id ||
                    undefined
                  }
                />
              )}
              {activeTab === 6 && (
                <PromptSettings
                  selectedUserId={
                    selectedUser?._id ||
                    selectedUser?.userId ||
                    selectedUser?.id ||
                    undefined
                  }
                />
              )}
              {activeTab === 7 && (
                <ChangePassword
                  selectedUserId={
                    selectedUser?._id ||
                    selectedUser?.userId ||
                    selectedUser?.id ||
                    undefined
                  }
                />
              )}
              {activeTab === 8 && (
                <ToolsSettings
                  selectedUserId={
                    selectedUser?._id ||
                    selectedUser?.userId ||
                    selectedUser?.id ||
                    undefined
                  }
                />
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default Users;
